import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnDestroy
} from '@angular/core';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MatDateFormats
} from '@angular/material/core';
import {
	MatCalendar,
	MatDatepickerIntl,
	yearsPerPage
} from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-custom-header',
	templateUrl: './custom-header.component.html',
	styleUrls: ['./custom-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomHeaderComponent<D> implements OnDestroy {
	private _destroyed = new Subject<void>();

	constructor(
		public _calendar: MatCalendar<D>,
		private _dateAdapter: DateAdapter<D>,
		private _intl: MatDatepickerIntl,
		@Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
		cdr: ChangeDetectorRef
	) {
		_calendar.stateChanges
			.pipe(takeUntil(this._destroyed))
			.subscribe(() => cdr.markForCheck());
	}

	ngOnDestroy() {
		this._destroyed.next();
		this._destroyed.complete();
	}

	get today() {
		return Date.now();
	}

	get periodButtonLabel(): string {
		return this._calendar.currentView == 'month'
			? this._intl.switchToMultiYearViewLabel
			: this._intl.switchToMonthViewLabel;
	}

	get periodButtonText(): string {
		if (this._calendar.currentView == 'month') {
			return this._dateAdapter
				.format(
					this._calendar.activeDate,
					this._dateFormats.display.monthYearLabel
				)
				.toLocaleUpperCase();
		}
		if (this._calendar.currentView == 'year') {
			return this._dateAdapter.getYearName(this._calendar.activeDate);
		}

		const activeYear = this._dateAdapter.getYear(this._calendar.activeDate);
		const minYearOfPage =
			activeYear -
			this.getActiveOffset(
				this._dateAdapter,
				this._calendar.activeDate,
				this._calendar.minDate,
				this._calendar.maxDate
			);
		const maxYearOfPage = minYearOfPage + yearsPerPage - 1;
		const minYearName = this._dateAdapter.getYearName(
			this._dateAdapter.createDate(minYearOfPage, 0, 1)
		);
		const maxYearName = this._dateAdapter.getYearName(
			this._dateAdapter.createDate(maxYearOfPage, 0, 1)
		);
		return this._intl.formatYearRange(minYearName, maxYearName);
	}

	getActiveOffset<D>(
		dateAdapter: DateAdapter<D>,
		activeDate: D,
		minDate: D | null,
		maxDate: D | null
	): number {
		const activeYear = dateAdapter.getYear(activeDate);
		return this.euclideanModulo(
			activeYear - this.getStartingYear(dateAdapter, minDate, maxDate),
			yearsPerPage
		);
	}

	getStartingYear<D>(
		dateAdapter: DateAdapter<D>,
		minDate: D | null,
		maxDate: D | null
	): number {
		let startingYear = 0;
		if (maxDate) {
			const maxYear = dateAdapter.getYear(maxDate);
			startingYear = maxYear - yearsPerPage + 1;
		} else if (minDate) {
			startingYear = dateAdapter.getYear(minDate);
		}
		return startingYear;
	}

	euclideanModulo(a: number, b: number): number {
		return ((a % b) + b) % b;
	}

	currentPeriodClicked(): void {
		this._calendar.currentView =
			this._calendar.currentView == 'month' ? 'multi-year' : 'month';
	}

	customPrev(): void {
		this.previousClicked();
	}

	customNext(): void {
		this.nextClicked();
	}

	previousClicked(): void {
		this._calendar.activeDate =
			this._calendar.currentView == 'month'
				? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
				: this._dateAdapter.addCalendarYears(
						this._calendar.activeDate,
						this._calendar.currentView == 'year' ? -1 : -yearsPerPage
					);
	}

	nextClicked(): void {
		this._calendar.activeDate =
			this._calendar.currentView == 'month'
				? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
				: this._dateAdapter.addCalendarYears(
						this._calendar.activeDate,
						this._calendar.currentView == 'year' ? 1 : yearsPerPage
					);
	}
}
